import styles from './sidepane.module.scss';
import { useAuth0 } from '@auth0/auth0-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import { store } from 'stores/Store';
import { observer } from 'mobx-react';
import Select from 'react-select';
import { voiceModeLocalStorage } from 'stores/Localstorage';

import useExternalApiService from 'helpers/ExternalApiService';

import { init, setUserId, logEvent } from '@amplitude/analytics-browser'; // ← browser SDK
import { Center, Container, Loader, Stack, Collapse } from '@mantine/core';
import { openDrawerModal } from 'components/DrawerModal';
import OppsAndRisk from 'Modules/OppsAndRisks';
import ExportCheckInModal from 'components/ExportCheckInModal/ExportCheckInModal';
import { MessageSquarePlus, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

interface SidepaneContentProps {
  togglePane: () => void;
}

const SidepaneContent: React.FC<SidepaneContentProps> = observer(({ togglePane }) => {

  const { logout } = useAuth0();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);

  const [currentVoiceMode, setCurrentVoiceMode] = React.useState(voiceModeLocalStorage.get() ?? { value: 'TEXT', label: 'Text Response' });
  const [collapsed, setCollapsed] = React.useState(true);
  const [hoveredConvId, setHoveredConvId] = React.useState<number | null>(null);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  // Detect mobile screen size
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper to track all button clicks with a consistent event
  const trackClick = (buttonName: string) => {
    const label = 'Sidepane Button: ' + buttonName
    logEvent(label, { buttonName });
  };

  // Helper to format dates for conversation grouping
  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const isImpersonateListLoading = !(!!store?.user?.impersonateList);

  //console.log("sidepane code userName: ", store.user?.userName);

  // Load conversations when component mounts or user changes
  React.useEffect(() => {
    if (store.user) {
      store.loadConversations(postData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.user, store.impersonatedUser]);
  // Note: postData removed from dependencies to prevent infinite loop
  // postData is a stable function reference from useExternalApiService

  const handleExportWithDateRange = async (startDate: string, endDate: string) => {
    trackClick('Export Check-In CSV');
    try {
      // Calls the POST endpoint with date range and expects the CSV text in the response
      const { csv } = await postData('/api/e/winback_export_checkins', {
        fmc_email: store.impersonatedUser,
        user_name: store.user?.userName,
        start_date: startDate,
        end_date: endDate,
      });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${store.impersonatedUser?.split('@')[0]}_checkins_${startDate}_to_${endDate}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Sorry, could not download the report.');
    }
  };

  const fetchHeroSkuHeatmap = async () => {
    trackClick('Hero SKU Heatmap');
    try {
      // Call the standalone heatmap endpoint
      const response = await postData('/api/e/winback_hero_sku_heatmap', {
        fmc_email: store.impersonatedUser,
        user_name: store.user?.userName,
      });

      if (response.success) {
        // Store heatmap data in MobX store
        store.setHeatmap(response.heatmap);
        // Close sidepane
        togglePane();
        // Navigate to chatbot with showHeatmap parameter
        navigate(`/?${new URLSearchParams({ showHeatmap: 'true' }).toString()}`);
      } else {
        console.error('Heatmap fetch failed:', response.message);
        alert('Sorry, could not load the Hero SKU Heatmap.');
      }
    } catch (error) {
      console.error('Heatmap fetch error:', error);
      alert('Sorry, could not load the Hero SKU Heatmap.');
    }
  };

  const doLogout = () => {
    trackClick('Log Out');
    logout({ logoutParams: { returnTo: window.location.origin } })
      .then(() => {
        // localstorage clean up if necessary
        // clean up map localstorage setups
        window.localStorage.removeItem("map-filters");
        window.localStorage.removeItem("map-colors");
      });
  }


  React.useEffect(() => {
    voiceModeLocalStorage.save(currentVoiceMode ?? 'TEXT');
  }, [currentVoiceMode]);

  return (
    <>
      <div className={styles.contentWrapper}>
      <div className={styles.row}>
        {store.user?.userPicture ? (
          <img
            className={styles.userPicture}
            src={store.user.userPicture}
          />
        ) :
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#E95540',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '24px',
              fontWeight: 'bold',
              minWidth: '48px',
              minHeight: '48px',
              flexShrink: 0
            }}
          >
            {store.user?.userFriendlyName?.charAt(0) || ''}
          </div>
        }
        <div className={styles.column}>
          <Text type='header'>{store.user?.userFriendlyName || ''}</Text>
          <Text type='smaller'>{store.user?.userName || ''}</Text>
        </div>
      </div>
      <div className={styles.listWrapper}>
        {store.user && Array.isArray(store.user.impersonateList) && store.user.impersonateList.length > 0 && (
          <>
            <Text type='primary'>View as User</Text>
            <Select
              isLoading={isImpersonateListLoading}
              isDisabled={isImpersonateListLoading}
              value={store.impersonatedUser ? { value: store.impersonatedUser, label: store.impersonatedUser } : null}
              onChange={(option: any) => store.setImpersonatedUser(option?.value || '')}
              options={[
                { value: store.user.userName, label: store.user.userName },
                ...store.user.impersonateList.map((fmc) => ({
                  value: fmc,
                  label: fmc
                }))
              ]}
              isClearable
              placeholder="Select a user..."
              styles={{
                control: (base) => ({ ...base, fontSize: '18px' }),
                option: (base) => ({ ...base, fontSize: '18px' }),
                singleValue: (base) => ({ ...base, fontSize: '18px' }),
                placeholder: (base) => ({ ...base, fontSize: '18px' }),
                menu: (base) => ({ ...base, fontSize: '18px' })
              }}
            />
          </>
        )}
      </div>

      {/* Separator 1: After "View as User" section */}
      <div className={styles.divider} />

      {/* New Chat Button */}
      <div className={styles.listWrapper}>
        <button
          onClick={() => {
            trackClick('New Chat');
            store.createNewConversation();
          }}
          style={{
            background: 'transparent',
            border: 'none',
            textAlign: 'left',
            padding: '0',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#0B2240',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            transition: 'transform 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <MessageSquarePlus size={18} style={{ marginRight: '8px', flexShrink: 0 }} />
          New chat
        </button>
      </div>

      {/* Separator 2: After "New Chat" button */}
      <div className={styles.divider} />

      {/* Recent Conversations - Collapsible Section */}
      <div className={styles.listWrapper}>
        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '0',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#F9FAFB';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text type='primary'>Recent</Text>
            <div style={{
              background: '#D1D5DB',
              borderRadius: '10px',
              padding: '1px 6px',
              fontSize: '10px',
              color: '#6B7280',
            }}>
              {store.conversations?.length || 0}
            </div>
          </div>
          {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        </button>

        <Collapse in={!collapsed}>
          <div style={{ maxHeight: '240px', overflowY: 'auto', overflowX: 'hidden' }}>
            {store.conversations && store.conversations.length > 0 ? (
              (() => {
                // Group conversations by date
                const grouped = store.conversations.reduce((groups: Record<string, typeof store.conversations>, conv) => {
                  const dateKey = formatDate(conv.lastUpdated);
                  if (!groups[dateKey]) {
                    groups[dateKey] = [];
                  }
                  groups[dateKey].push(conv);
                  return groups;
                }, {});

                // Sort date groups chronologically (most recent first)
                // Date order: Today > Yesterday > Oct 31 > Oct 30 > Oct 29 ...
                const sortedEntries = Object.entries(grouped).sort(([dateA], [dateB]) => {
                  const order = ['Today', 'Yesterday'];
                  const indexA = order.indexOf(dateA);
                  const indexB = order.indexOf(dateB);

                  // Both are Today/Yesterday - use position in order array
                  if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                  // A is Today/Yesterday, B is a date
                  if (indexA !== -1) return -1;
                  // B is Today/Yesterday, A is a date
                  if (indexB !== -1) return 1;
                  // Both are dates - compare chronologically (more recent first)
                  // Parse dates like "Oct 31" and compare
                  const parseDate = (str: string) => new Date(str + ', ' + new Date().getFullYear());
                  return parseDate(dateB).getTime() - parseDate(dateA).getTime();
                });

                return sortedEntries.map(([dateLabel, convs]) => (
                  <div key={dateLabel} style={{ marginBottom: '16px' }}>
                    {/* Date label */}
                    <div style={{
                      fontSize: '9px',
                      color: '#9CA3AF',
                      marginBottom: '4px',
                      paddingLeft: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontWeight: 600,
                    }}>
                      {dateLabel}
                    </div>

                    {/* Conversations under this date */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {convs.map((conv) => (
                        <div
                          key={conv.id}
                          style={{ position: 'relative' }}
                          onMouseEnter={() => setHoveredConvId(conv.id)}
                          onMouseLeave={() => setHoveredConvId(null)}
                        >
                          <button
                            onClick={async () => {
                              if (!apiUrl) {
                                return;
                              }
                              try {
                                await store.loadConversation(conv.id, apiUrl, postData);
                                togglePane(); // Close sidebar after loading conversation
                              } catch (error) {
                                // Silently handle error
                              }
                            }}
                            style={{
                              background: store.activeConversationId === conv.id
                                ? '#F3F4F6'
                                : hoveredConvId === conv.id
                                ? '#F9FAFB'
                                : 'transparent',
                              border: 'none',
                              textAlign: 'left',
                              padding: '4px 6px',
                              paddingRight: hoveredConvId === conv.id ? '24px' : '6px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              color: store.activeConversationId === conv.id ? 'black' : '#4B5563',
                              width: '100%',
                              borderRadius: '3px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              transition: 'all 0.1s ease',
                              display: 'block',
                            }}
                          >
                            {conv.title}
                          </button>

                          {/* Delete button - appears on hover (desktop) or always visible (mobile) */}
                          {(hoveredConvId === conv.id || isMobile) && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                trackClick('Archive Conversation');

                                try {
                                  // Call backend API with authentication
                                  await postData('/api/archive_conversation', {
                                    conversation_id: conv.id,
                                    fmc_email: store.impersonatedUser || store.user?.userName
                                  });

                                  // Update local state on success
                                  store.archiveConversation(conv.id);
                                } catch (error) {
                                  console.error('Failed to archive conversation:', error);
                                }
                              }}
                              style={{
                                position: 'absolute',
                                right: '2px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '3px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '2px',
                                transition: 'background 0.1s ease',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#FEE2E2';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              <Trash2 size={10} color="#DC2626" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()
            ) : (
              <div style={{ fontSize: '11px', color: '#9CA3AF', textAlign: 'center', padding: '20px 0' }}>
                No conversations yet
              </div>
            )}
          </div>
        </Collapse>
      </div>

      {/* Separator 3: After "Recent" section */}
      <div className={styles.divider} />

      <div className={styles.listWrapper}>
        <Text type='primary'>Quick-Links</Text>
        {isImpersonateListLoading ? (
          <Container>
            <Stack>
              <Center>
                <Loader />
              </Center>
              <Center>
                <p style={{ textAlign: "center"}}>
                  AI is being updated, please reload in a few minutes
                </p>
              </Center>
            </Stack>
          </Container>
        ) : (
          <>
            {(!store.user?.isAdmin || store.impersonatedUser !== store.user?.userName) && (
              <Button type='transparent' onClick={fetchHeroSkuHeatmap}>
                Hero SKU Heatmap
              </Button>
            )}
            {((!store.user?.isAdmin || store.impersonatedUser !== store.user?.userName) && store.user?.earlyRelease) && (
              <Button type='transparent' onClick={() => {
                trackClick('Practitioner List');
                togglePane();
                navigate(`/?${new URLSearchParams({ showPractitioners: 'true' }).toString()}`)
              }
              }>
                Practitioner List
              </Button>
            )}
            {store.user?.earlyRelease && (
              <Button type='transparent' onClick={() => {
                togglePane();
                openDrawerModal(<OppsAndRisk />)
              }
              }>
                Opportunities & Risks List
              </Button>
            )}
            {((!store.user?.isAdmin || store.impersonatedUser !== store.user?.userName) && store.user?.earlyRelease) && (
              <Button type='transparent' onClick={() => {
                trackClick('My Routes');
                togglePane();
                navigate(`/?${new URLSearchParams({ showRoutes: 'true' }).toString()}`)
              }
              }>
                My Routes
              </Button>
            )}
            {((!store.user?.isAdmin || store.impersonatedUser !== store.user?.userName) && store.user?.earlyRelease) && (
              <Button type='transparent' onClick={() => {
                trackClick('Territory Map');
                togglePane();
                navigate(`/mapView`)
              }
              }>
                Territory Map
              </Button>
            )}
            {((!store.user?.isAdmin || store.impersonatedUser !== store.user?.userName) && store.user?.earlyRelease) && (
              <Button type='transparent' onClick={() => {
                trackClick('Create Lead');
                togglePane();
                navigate(`/?${new URLSearchParams({ showNewLead: 'true' }).toString()}`)
              }
              }>
                Create Lead
              </Button>
            )}
            {store.user?.earlyRelease && (
              <Button type='transparent' onClick={() => {
                togglePane();
                openDrawerModal(
                  <ExportCheckInModal onExport={handleExportWithDateRange} />
                );
              }
              }>
                Export Check-In CSV
              </Button>
            )}
          </>
        )}
      </div>

      {/* Separator 3: After "Quick-Links" section */}
      <div className={styles.divider} />

      <div className={styles.listWrapper}>
        <>
          <Text type='primary'>Voice Mode</Text>
          <Select
            options={[
              { value: 'TEXT', label: 'Text Response' },
              { value: 'VOICE', label: 'Voice Response' }
            ]}
            value={currentVoiceMode}
            onChange={(option: any) => {
              setCurrentVoiceMode(option)
            }}
            styles={{
              control: (base) => ({ ...base, fontSize: '18px' }),
              option: (base) => ({ ...base, fontSize: '18px' }),
              singleValue: (base) => ({ ...base, fontSize: '18px' }),
              placeholder: (base) => ({ ...base, fontSize: '18px' }),
              menu: (base) => ({ ...base, fontSize: '18px' })
            }}
          />
        </>
      </div>

      {/* Separator 4: After "Voice Mode" section, before "Log out" */}
      <div className={styles.divider} />

      <div style={{
        width: '80%',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        justifyContent: 'start',
        gap: '12px',
        margin: '16px 20px 0 20px'
      }}>
        <Button
          onClick={doLogout}
          type='transparent'>Log out</Button>
      </div>

      {/* Separator to create equal spacing below Log out */}
      <div style={{
        width: '100%',
        height: '1px',
        backgroundColor: '#E5E7EB',
        marginTop: '16px',
        flexShrink: 0
      }} />
    </div>
    </>
  )
})

export default SidepaneContent;